import React, { useEffect, useMemo, useState } from "react";
import { isValidWord } from "../services/dictionary";
import { normalizeWord, scoreWord } from "../utils/scrabble";
import { useI18n } from "../i18n/I18nContext";
import { getWordDefinition, type DefinitionResult } from "../services/openai";

type ResultState = {
  normalized: string;
  valid: boolean | null;
  total: number;
  breakdown: { letter: string; value: number; isBlank: boolean }[];
  error?: string;
};

type WordSlotProps = {
  onCheck: (word: string, isValid: boolean) => void;
};

function WordSlot({ onCheck }: WordSlotProps) {
  const { t } = useI18n();
  const [input, setInput] = useState("");
  const [result, setResult] = useState<ResultState>({
    normalized: "",
    valid: null,
    total: 0,
    breakdown: []
  });
  const [busy, setBusy] = useState(false);
  const cleaned = useMemo(() => normalizeWord(input), [input]);

  useEffect(() => {
    const { total, parts } = scoreWord(input);
    setResult(prev => ({ ...prev, total, breakdown: parts, normalized: cleaned }));
  }, [input, cleaned]);

  async function handleCheck(e?: React.FormEvent) {
    e?.preventDefault();
    const word = cleaned;

    if (!word) {
      setResult({
        normalized: "",
        valid: null,
        total: 0,
        breakdown: [],
        error: t('wordValidatorErrorEmpty')
      });
      return;
    }

    if (/[*?]/.test(word)) {
      setResult(prev => ({
        ...prev,
        valid: null,
        error: t('wordValidatorErrorBlanks')
      }));
      return;
    }

    setBusy(true);
    try {
      const ok = await isValidWord(word);
      setResult(prev => ({ ...prev, valid: ok, error: undefined }));
      onCheck(word, ok);
    } catch {
      setResult(prev => ({ ...prev, valid: null, error: t('wordValidatorErrorLookupFailed') }));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="bg-surface/80 backdrop-blur-theme border border-subtle rounded-card p-6 shadow-card card-interactive transition-all">
      <h3 className="text-lg font-semibold mb-4 text-secondary">{t('wordValidatorCheckWord')}</h3>

      <form onSubmit={handleCheck} className="flex gap-2 mb-4">
        <input
          className="flex-1 border-2 border-input-border bg-input-bg rounded-lg px-4 py-2 focus:outline-none focus:border-validator transition-colors text-primary"
          placeholder={t('wordValidatorInputPlaceholder')}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          aria-label="Word input"
        />
        <button
          type="submit"
          className="bg-validator hover:bg-validator-hover text-white font-medium px-6 py-2 rounded-btn disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          disabled={busy || !cleaned}
        >
          {busy ? t('wordValidatorCheckingButton') : t('wordValidatorCheckButton')}
        </button>
      </form>

      {result.error && (
        <div className="bg-error-subtle border border-error-border text-error-text px-4 py-2 rounded mb-4">
          {result.error}
        </div>
      )}

      {result.normalized && (
        <div className="space-y-3">
          <div className="flex items-center justify-between pb-3 border-b border-subtle">
            <span className="text-secondary">{t('wordValidatorLabelWord')}</span>
            <span className="font-bold text-xl text-primary">{result.normalized}</span>
          </div>

          <div className="flex items-center justify-between pb-3 border-b border-subtle">
            <span className="text-secondary">{t('wordValidatorLabelValidity')}</span>
            <span className="font-semibold">
              {result.valid === null ? (
                <span className="text-muted">{t('wordValidatorNotChecked')}</span>
              ) : result.valid ? (
                <span className="text-success flex items-center gap-1">
                  <span>✓</span> {t('wordValidatorValid')}
                </span>
              ) : (
                <span className="text-error flex items-center gap-1">
                  <span>✗</span> {t('wordValidatorNotInDictionary')}
                </span>
              )}
            </span>
          </div>

          <div className="flex items-center justify-between pb-3 border-b border-subtle">
            <span className="text-secondary">{t('wordValidatorLabelBaseScore')}</span>
            <span className="font-bold text-2xl text-validator-text">{result.total}</span>
          </div>

          {result.breakdown.length > 0 && (
            <div>
              <h4 className="font-semibold text-secondary mb-2">{t('wordValidatorLetterBreakdownTitle')}</h4>
              <div className="grid grid-cols-2 gap-2">
                {result.breakdown.map((p, i) => (
                  <div
                    key={`${p.letter}-${i}`}
                    className="flex justify-between items-center bg-surface-alt px-3 py-2 rounded"
                  >
                    <span className="font-mono font-bold text-lg">
                      {p.letter} {p.isBlank && <span className="text-sm text-muted">{t('wordValidatorBlankLabel')}</span>}
                    </span>
                    <span className="text-secondary">{p.value} {t('wordValidatorPointsSuffix')}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

type DefinitionCardProps = {
  word: string | null;
  isValid: boolean | null;
};

function DefinitionCard({ word, isValid }: DefinitionCardProps) {
  const { t } = useI18n();
  const [definition, setDefinition] = useState<DefinitionResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!word || isValid === false) {
      setDefinition(null);
      setError(null);
      return;
    }

    const fetchDefinition = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await getWordDefinition(word);
        setDefinition(result);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch definition');
        setDefinition(null);
      } finally {
        setLoading(false);
      }
    };

    fetchDefinition();
  }, [word, isValid]);

  return (
    <div className="bg-surface/80 backdrop-blur-theme border border-subtle rounded-card p-6 shadow-card card-interactive transition-all">
      <h3 className="text-lg font-semibold mb-4 text-secondary flex items-center gap-2">
        <span className="text-magic">AI</span> {t('definitionCardTitle')}
      </h3>

      {!word && (
        <div className="text-center py-8 text-muted">
          <p className="text-lg mb-2">{t('definitionCardNoWord')}</p>
          <p className="text-sm">{t('definitionCardPrompt')}</p>
        </div>
      )}

      {word && isValid === false && (
        <div className="text-center py-8 text-muted">
          <p className="text-lg mb-2">{t('definitionCardInvalidWord')}</p>
          <p className="text-sm">{t('definitionCardInvalidPrompt')}</p>
        </div>
      )}

      {loading && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-magic border-t-transparent mb-3"></div>
          <p className="text-secondary">{t('definitionCardLoading')}</p>
        </div>
      )}

      {error && (
        <div className="bg-error-subtle border border-error-border text-error-text px-4 py-3 rounded-lg">
          <p className="font-semibold">{t('definitionCardError')}</p>
          <p className="text-sm">{error}</p>
        </div>
      )}

      {definition && !loading && (
        <div className="space-y-4">
          <div className="pb-3 border-b border-subtle">
            <div className="flex items-center gap-3 mb-2">
              <span className="font-mono font-bold text-2xl text-primary">
                {definition.word}
              </span>
              {definition.partOfSpeech && (
                <span className="text-sm italic text-magic-text bg-magic-subtle px-2 py-1 rounded">
                  {definition.partOfSpeech}
                </span>
              )}
            </div>
            <p className="text-secondary">{definition.definition}</p>
          </div>

          {definition.examples && definition.examples.length > 0 && (
            <div className="pb-3 border-b border-subtle">
              <h4 className="font-semibold text-secondary mb-2">{t('definitionCardExamples')}</h4>
              <ul className="space-y-1">
                {definition.examples.map((example, i) => (
                  <li key={i} className="text-muted text-sm italic pl-4 border-l-2 border-magic/30">
                    "{example}"
                  </li>
                ))}
              </ul>
            </div>
          )}

          {definition.etymology && (
            <div className="pb-3 border-b border-subtle">
              <h4 className="font-semibold text-secondary mb-2">{t('definitionCardEtymology')}</h4>
              <p className="text-muted text-sm">{definition.etymology}</p>
            </div>
          )}

          {definition.scrabbleTip && (
            <div className="bg-validator-subtle border border-validator/20 rounded-lg p-4">
              <h4 className="font-semibold text-validator-text mb-1 flex items-center gap-2">
                <span>🎯</span> {t('definitionCardScrabbleTip')}
              </h4>
              <p className="text-validator-text text-sm">{definition.scrabbleTip}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function WordLookup() {
  const { t } = useI18n();
  const [checkedWord, setCheckedWord] = useState<string | null>(null);
  const [wordValidity, setWordValidity] = useState<boolean | null>(null);

  const handleWordCheck = (word: string, isValid: boolean) => {
    setCheckedWord(word);
    setWordValidity(isValid);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-page via-surface to-page py-8 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold font-heading text-primary mb-3">{t('wordValidatorTitle')}</h1>
          <p className="text-lg text-secondary max-w-2xl mx-auto">
            {t('wordValidatorSubtitle')}
          </p>
        </div>

        {/* Info Section */}
        <div className="bg-surface/80 backdrop-blur-theme border border-subtle p-4 mb-8 rounded-card">
          <h2 className="font-semibold text-primary mb-2">{t('wordValidatorHowItWorksTitle')}</h2>
          <ul className="text-secondary space-y-1 text-sm">
            <li>• {t('wordValidatorHowItWorks1')}</li>
            <li>• {t('wordValidatorHowItWorks2')}</li>
            <li>• {t('wordValidatorHowItWorks3')}</li>
            <li>• {t('wordValidatorHowItWorks4')}</li>
            <li>• {t('wordValidatorHowItWorks5')}</li>
          </ul>
        </div>

        {/* Word Validator and Definition */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <WordSlot onCheck={handleWordCheck} />
          <DefinitionCard word={checkedWord} isValid={wordValidity} />
        </div>

        {/* Footer Info */}
        <div className="bg-surface/80 backdrop-blur-theme rounded-card border border-subtle p-6 shadow-card">
          <h3 className="font-semibold text-primary mb-3">{t('wordValidatorScoringRefTitle')}</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
            <div>
              <div className="font-semibold text-secondary mb-1">{t('wordValidatorPointsLabel1')}</div>
              <div className="text-secondary">A, E, I, O, U, L, N, S, T, R</div>
            </div>
            <div>
              <div className="font-semibold text-secondary mb-1">{t('wordValidatorPointsLabel2')}</div>
              <div className="text-secondary">D, G</div>
            </div>
            <div>
              <div className="font-semibold text-secondary mb-1">{t('wordValidatorPointsLabel3')}</div>
              <div className="text-secondary">B, C, M, P</div>
            </div>
            <div>
              <div className="font-semibold text-secondary mb-1">{t('wordValidatorPointsLabel4')}</div>
              <div className="text-secondary">F, H, V, W, Y</div>
            </div>
            <div>
              <div className="font-semibold text-secondary mb-1">{t('wordValidatorPointsLabel5')}</div>
              <div className="text-secondary">K</div>
            </div>
            <div>
              <div className="font-semibold text-secondary mb-1">{t('wordValidatorPointsLabel8')}</div>
              <div className="text-secondary">J, X</div>
            </div>
            <div>
              <div className="font-semibold text-secondary mb-1">{t('wordValidatorPointsLabel10')}</div>
              <div className="text-secondary">Q, Z</div>
            </div>
          </div>
          <p className="text-sm text-muted mt-4">
            {t('wordValidatorNoteBaseOnly')}
          </p>
        </div>
      </div>
    </div>
  );
}